import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TableProps } from 'antd';
import {
  DeleteOutlined,
  PrinterOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { financeApi, type CashHandover } from '@/api/finance';
import { PageHeader } from '@/shared/components/page-header';
import { openPdf } from '@/shared/lib/download';
import { extractError } from '@/shared/lib/error-message';
import { useAbility } from '@/auth/ability-provider';

const PKR = (n: string | number) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

interface FormValues {
  receivedBy: string;
  handedAmount: number;
  notes?: string;
}

export function FinanceCashHandoverPage() {
  const ability = useAbility();
  const canCreate = ability.can('create', 'Finance');
  const canDelete = ability.can('delete', 'Finance');

  const [bankId, setBankId] = useState<number | undefined>();
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [historyRange, setHistoryRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [form] = Form.useForm<FormValues>();

  const qc = useQueryClient();

  // PDF must be fetched with the JWT — a plain <a href> hits the API
  // unauthenticated and 401s.
  const openVoucher = (id: number) =>
    openPdf(`/reports/exports/cash-handover/${id}.pdf`).catch((e) =>
      message.error(extractError(e, 'Could not open voucher PDF')),
    );

  const banksQuery = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: () => financeApi.listBankAccounts(),
  });

  // Auto-select the first cash account once banks load.
  if (!bankId && banksQuery.data && banksQuery.data.length > 0) {
    const firstCash = banksQuery.data.find((b) => b.cash && b.active);
    if (firstCash) setBankId(firstCash.id);
  }

  const summaryQuery = useQuery({
    queryKey: ['finance', 'cash-handover', 'summary', bankId, date.format('YYYY-MM-DD')],
    queryFn: () => financeApi.cashHandoverSummary(bankId!, date.format('YYYY-MM-DD')),
    enabled: !!bankId,
  });

  const historyQuery = useQuery({
    queryKey: [
      'finance',
      'cash-handover',
      'list',
      historyRange[0].format('YYYY-MM-DD'),
      historyRange[1].format('YYYY-MM-DD'),
    ],
    queryFn: () =>
      financeApi.listCashHandovers(
        historyRange[0].format('YYYY-MM-DD'),
        historyRange[1].format('YYYY-MM-DD'),
      ),
  });

  const createMutation = useMutation({
    mutationFn: (body: FormValues) =>
      financeApi.createCashHandover({
        bankAccountId: bankId!,
        handoverDate: date.format('YYYY-MM-DD'),
        receivedBy: body.receivedBy,
        handedAmount: String(body.handedAmount),
        notes: body.notes,
      }),
    onSuccess: (h) => {
      message.success(`Handover ${h.voucherNumber} recorded`);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['finance', 'cash-handover'] });
      Modal.confirm({
        title: 'Open handover voucher PDF?',
        content: `Voucher ${h.voucherNumber} for ₨ ${PKR(h.handedAmount)} handed to ${h.receivedBy}.`,
        okText: 'Open PDF',
        cancelText: 'Close',
        onOk: () => openVoucher(h.id),
      });
    },
    onError: (e) => message.error(extractError(e, 'Could not record handover')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => financeApi.deleteCashHandover(id),
    onSuccess: () => {
      message.success('Handover deleted');
      qc.invalidateQueries({ queryKey: ['finance', 'cash-handover'] });
    },
    onError: (e) => message.error(extractError(e, 'Delete failed')),
  });

  const summary = summaryQuery.data;
  const expected = summary ? Number(summary.expectedAmount) : 0;

  const historyColumns: TableProps<CashHandover>['columns'] = [
    {
      title: 'Voucher',
      dataIndex: 'voucherNumber',
      render: (v: string, row) => (
        <Typography.Link onClick={() => openVoucher(row.id)}>{v}</Typography.Link>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'handoverDate',
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    { title: 'Cashier', dataIndex: 'cashierName' },
    { title: 'Received by', dataIndex: 'receivedBy' },
    {
      title: 'Expected',
      dataIndex: 'expectedAmount',
      align: 'right' as const,
      render: (v: string) => `₨ ${PKR(v)}`,
    },
    {
      title: 'Handed',
      dataIndex: 'handedAmount',
      align: 'right' as const,
      render: (v: string) => <Typography.Text strong>₨ {PKR(v)}</Typography.Text>,
    },
    {
      title: 'Variance',
      dataIndex: 'variance',
      align: 'right' as const,
      render: (v: string) => {
        const n = Number(v);
        if (n === 0) return <Tag color="success">balanced</Tag>;
        const color = n > 0 ? 'gold' : 'red';
        const label = n > 0 ? 'over' : 'short';
        return <Tag color={color}>{label} ₨ {PKR(Math.abs(n))}</Tag>;
      },
    },
    {
      title: '',
      width: 100,
      align: 'right' as const,
      render: (_, row) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => openVoucher(row.id)}
          />
          {canDelete && (
            <Popconfirm
              title="Delete this handover?"
              description="The voucher and audit record will be soft-deleted."
              onConfirm={() => deleteMutation.mutate(row.id)}
            >
              <Button size="small" icon={<DeleteOutlined />} danger />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Module / Finance"
        title="Cash handover"
        description="Day-end voucher. Opening + today's receipts − today's payments = expected. Cashier counts the cash, records the handover, and prints the voucher to give with the cash. The handed amount posts to the ledger as an owner's drawing, so the till balance reflects what's left in the drawer."
      />

      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={10}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Cash account
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Choose cash account"
              loading={banksQuery.isLoading}
              value={bankId}
              onChange={setBankId}
              options={(banksQuery.data ?? [])
                .filter((b) => b.cash && b.active)
                .map((b) => ({ value: b.id, label: b.accountName }))}
            />
          </Col>
          <Col xs={24} md={8}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Handover date
            </Typography.Text>
            <DatePicker
              style={{ width: '100%' }}
              value={date}
              onChange={(d) => d && setDate(d)}
              allowClear={false}
              disabledDate={(d) => d.isAfter(dayjs(), 'day')}
            />
          </Col>
        </Row>
      </Card>

      {summary && (
        <Card title={`Day-end cash position — ${dayjs(summary.date).format('D MMM YYYY')}`}>
          <Row gutter={[16, 12]}>
            <Col xs={12} md={6}>
              <Statistic title="Opening cash" value={`₨ ${PKR(summary.openingBalance)}`} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Today's receipts"
                value={`₨ ${PKR(summary.totalReceipts)}`}
                styles={{ content: { color: '#389e0d' } }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Today's payments"
                value={`₨ ${PKR(summary.totalPayments)}`}
                styles={{ content: { color: '#cf1322' } }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Expected to hand over"
                value={`₨ ${PKR(summary.expectedAmount)}`}
                styles={{ content: { fontWeight: 600 } }}
              />
            </Col>
          </Row>
          {expected < 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="Expected handover is negative — the till is short: payments (and earlier handovers) exceed opening cash plus today's receipts."
            />
          )}
        </Card>
      )}

      {canCreate && (
        <Card title="Record handover">
          <Form<FormValues>
            form={form}
            layout="vertical"
            onFinish={(v) => createMutation.mutate(v)}
            initialValues={{ receivedBy: 'Principal' }}
          >
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="receivedBy"
                  label="Received by"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder="e.g. Principal" maxLength={160} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="handedAmount"
                  label="Cash counted / handed (₨)"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={50}
                    precision={2}
                    placeholder={summary ? PKR(summary.expectedAmount) : ''}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label=" " colon={false}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={createMutation.isPending}
                    disabled={!bankId}
                  >
                    Record &amp; print
                  </Button>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="notes" label="Notes (optional)">
              <Input.TextArea
                rows={2}
                maxLength={2000}
                placeholder="Anything worth noting — short over/short reason, denomination, etc."
              />
            </Form.Item>
          </Form>
        </Card>
      )}

      <Card
        title="History"
        extra={
          <DatePicker.RangePicker
            value={historyRange}
            onChange={(v) => v && v[0] && v[1] && setHistoryRange([v[0], v[1]])}
            allowClear={false}
          />
        }
      >
        {historyQuery.data && historyQuery.data.length === 0 ? (
          <Empty description="No handovers in this range" />
        ) : (
          <Table<CashHandover>
            rowKey="id"
            dataSource={historyQuery.data ?? []}
            loading={historyQuery.isFetching}
            columns={historyColumns}
            size="small"
            pagination={{ pageSize: 20 }}
          />
        )}
      </Card>
    </div>
  );
}
